import {
  ActionViewModel,
  ActionViewModelImplementation,
  durationInSecondsOrZero,
  Effector,
  secondsToDuration,
  uuidOrNewUuid,
} from "./ActionViewModel";
import { Field } from "formik";
import {
  FormControl,
  FormLabel,
  InputAdornment,
  TextField,
} from "@mui/material";
import { functionNameForScvInputId } from "portal/utils/atkEquipment";
import { positionMonitoredScvEffectors, StartDelayInput } from "./utils";
import { QuickAction } from "protos/atk/activities";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import { useRandomId } from "portal/utils/hooks/useRandomId";
import { useTranslation } from "react-i18next";
import React from "react";
import SetPositionIcon from "common/images/atk-actions/set-position-percent.svg?react";

class ScvSetPositionViewModel extends ActionViewModel<ScvSetPositionViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: {
      scvInputId: string;
      positionPct: number;
    }
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      scvMoveToPosition: {
        scvInputId: this.actionData.scvInputId,
        position: this.actionData.positionPct / 100,
      },
      startDelay: secondsToDuration(this.startDelaySec),
      carryOverIntoWorkTask: finalActionThisEffector,
    };
  }

  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.scvSetPosition",
        {
          function: functionNameForScvInputId(
            t,
            rigEffectiveConfig,
            this.actionData.scvInputId
          ),
          positionPct: this.actionData.positionPct,
        }
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.setPosition",
      { positionPct: this.actionData.positionPct }
    );
  }

  override components = scvSetPosition.components;
}
export const scvSetPosition: ActionViewModelImplementation<ScvSetPositionViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.setPosition",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: positionMonitoredScvEffectors(),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "scv") {
        throw new Error('scvSetPosition.newAction: effector type is not "scv"');
      }

      return new ScvSetPositionViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          scvInputId: effector.scvInputId,
          positionPct: 0,
        }
      );
    },
    fromQuickAction: (action, startInStageIndex) => {
      if (action.scvMoveToPosition) {
        return new ScvSetPositionViewModel(
          {
            type: "scv",
            scvInputId: action.scvMoveToPosition.scvInputId,
          },
          startInStageIndex,
          durationInSecondsOrZero(action.startDelay),
          action.actionId,
          {
            scvInputId: action.scvMoveToPosition.scvInputId,
            positionPct: action.scvMoveToPosition.position * 100,
          }
        );
      }
    },
    components: {
      Icon: SetPositionIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const positionLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormControl fullWidth>
              <FormLabel id={positionLabelId}>
                {t(
                  "views.autotractor.autonomousActivities.action.parameterName.position"
                )}
              </FormLabel>
              <Field
                component={TextField}
                name={basePath.actionData.positionPct.$path}
                className="mt-2"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t("utils.units.%")}
                      </InputAdornment>
                    ),
                    "aria-labelledby": positionLabelId,
                  },
                }}
              />
            </FormControl>
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
